// src/index.ts
import { XStorageError } from "@byted/tiktok-zti-storage-error";
var _LStorage = class _LStorage {
  constructor() {
    /**
     * Gets the value of the specified item from local storage.
     *
     * @example
     * const storage = new LStorage<{key: string}>();
     * storage.getItem("key").then(value => {
     *   // use the value...
     * }).catch(error => {
     *   // handle the error...
     * })
     *
     * @param {K} name - The name of the item to get.
     * @returns {Promise<T[K]>} - Returns a promise that resolves with the value of the item.
     */
    this.getItem = (name) => {
      return this.getItemByKeys([name]).then((res) => {
        return res[0];
      });
    };
    /**
     * Sets the value of the specified item in local storage.
     *
     * @example
     * const storage = new LStorage<{key: string}>();
     * storage.setItem("key", "value").then(() => {
     *   // item set successfully.
     * }).catch(error => {
     *   // handle the error...
     * })
     *
     * @param {K} name - The name of the item to set.
     * @param {T[K]} value - The value of the item to set.
     * @returns {Promise<void>} - Returns a promise that resolves when the item is set successfully.
     */
    this.setItem = (name, value) => {
      return this.setItemByKeys([[name, value]]).then((res) => {
        return res[0];
      });
    };
    this.getItemByKeys = (keys) => {
      return this.openLocalStorage().then((storage) => {
        return keys.map((name) => {
          const { data } = JSON.parse(storage.getItem(name) || "{}");
          return data;
        });
      });
    };
    this.setItemByKeys = (pairs) => {
      return this.openLocalStorage().then((storage) => {
        return pairs.map(([name, value]) => {
          const dataValue = typeof value === "string" ? JSON.stringify({ data: value }) : JSON.stringify({ data: value });
          storage.setItem(name, dataValue);
          return value;
        });
      });
    };
    /**
     * Removes the item with the specified name from local storage.
     *
     * @example
     * const storage = new LStorage<{key: string}>();
     * storage.removeItem("key").then(() => {
     *   // item removed successfully.
     * }).catch(error => {
     *   // handle the error...
     * })
     *
     * @param {K} name - The name of the item to remove.
     * @returns {Promise<void>} - Returns a promise that resolves when the item is removed successfully.
     */
    this.removeItem = (name) => {
      return this.openLocalStorage().then((storage) => {
        storage.removeItem(name);
      });
    };
    this.getKeys = () => {
      return this.openLocalStorage().then((storage) => {
        const keys = [];
        for (let i = 0; i < storage.length; i++) {
          const key = storage.key(i);
          if (key) {
            keys.push(storage.key(i));
          }
        }
        return keys;
      });
    };
    this.openLocalStorage = () => {
      if (!_LStorage.isSupport()) {
        return Promise.reject(new XStorageError(`LocalStorageNotSupport`));
      }
      return Promise.resolve(getLocalStorage());
    };
  }
};
/**
 * Checks if local storage is supported in the current browser.
 *
 * @example
 * if (LStorage.isSupport()) {
 *   // localStorage is supported
 * } else {
 *   // show error message to user
 * }
 *
 * @returns {boolean} - returns true if localStorage is supported, false otherwise.
 */
_LStorage.isSupport = () => {
  const storage = getLocalStorage();
  if (!storage)
    return false;
  try {
    const x = "__x_storage_test__";
    storage.setItem(x, x);
    const value = storage.getItem(x);
    storage.removeItem(x);
    return x === value;
  } catch (e) {
    return false;
  }
};
var LStorage = _LStorage;
var getLocalStorage = () => {
  return window.localStorage;
};
var src_default = LStorage;
export {
  src_default as default
};
